import { cognitoConfig, refreshSession, type Tokens } from "./cognito";

/**
 * Token storage and renewal, deliberately outside React so `lib/api` can await a
 * valid token without a hook. The Hosted UI flow discarded the refresh token and
 * signed users out after an hour; keeping it is what makes a session last.
 */
export const TOKEN_KEY = "alpha.idToken";
export const REFRESH_KEY = "alpha.refreshToken";

/** Renew this far ahead of `exp` so a request never leaves with a dead token. */
export const RENEWAL_MARGIN_MS = 60_000;

type Listener = () => void;

const listeners = new Set<Listener>();
let inFlight: Promise<string | null> | null = null;

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function announce(): void {
  for (const fn of listeners) fn();
}

/** Expiry in milliseconds since the epoch, or 0 when the token is unreadable. */
export function expiryOf(token: string): number {
  const part = token.split(".")[1];
  if (!part) return 0;
  try {
    const { exp } = JSON.parse(atob(part)) as { exp?: number };
    return typeof exp === "number" ? exp * 1000 : 0;
  } catch {
    return 0;
  }
}

export function needsRenewal(token: string): boolean {
  return expiryOf(token) - Date.now() < RENEWAL_MARGIN_MS;
}

export function currentIdToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function store(tokens: Tokens): void {
  localStorage.setItem(TOKEN_KEY, tokens.idToken);
  if (tokens.refreshToken) localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
  announce();
}

export function clear(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  announce();
}

async function renew(): Promise<string | null> {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) {
    clear();
    return null;
  }
  try {
    const tokens = await refreshSession(await cognitoConfig(), refreshToken);
    store(tokens);
    return tokens.idToken;
  } catch {
    // A refresh token that no longer works means the session is over. Clearing
    // shows the signed-out UI instead of a wall of 401s.
    clear();
    return null;
  }
}

/** Single-flight: a dashboard firing four queries at once triggers one refresh. */
export function forceRenewal(): Promise<string | null> {
  inFlight ??= renew().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

export async function getValidIdToken(): Promise<string | null> {
  const token = currentIdToken();
  if (!token) return null;
  if (!needsRenewal(token)) return token;
  return forceRenewal();
}
